(() => {
  const initCompare = (root) => {
    if (root.dataset.gm3CompareReady === 'true') return;
    root.dataset.gm3CompareReady = 'true';

    const sides = ['left', 'right'];
    const collapsedSpecLimit = 7;
    const specToggle = root.querySelector('[data-gm3-compare-spec-toggle]');
    const specToggleText = specToggle?.querySelector('[data-gm3-compare-spec-toggle-text]');
    const products = root.querySelector('.gm3-compare__products');
    const mobileQuery = window.matchMedia('(max-width: 1024px)');
    let specsExpanded = false;
    let stickyTicking = false;

    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const cartAddUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';

    const formatMoney = (cents) => {
      let value = '';
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        value = window.Shopify.formatMoney(cents, moneyFormat);
      } else {
        value = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: root.dataset.currency || 'USD',
          currencyDisplay: 'narrowSymbol',
        }).format((Number(cents) || 0) / 100);
      }

      return String(value).replace(/\bUS\s?\$/g, '$').replace(/\bUSD\s?/g, '');
    };

    const normalizeOptions = (variant) => {
      if (!variant) return [];
      if (Array.isArray(variant.options)) return variant.options;
      return [variant.option1, variant.option2, variant.option3].filter(Boolean);
    };

    const getVariantLabel = (variant) => {
      if (!variant) return '';
      return variant.title && variant.title !== 'Default Title' ? variant.title : '';
    };
    
    const getVariantImageUrl = (variant) => {
      const image = variant?.featured_image || variant?.featured_media?.preview_image || variant?.image;
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.src || image.url || image.preview_image?.src || image.preview_image?.url || '';
    };

    const updatePrice = (panel, variant) => {
      const current = panel.querySelector('[data-gm3-compare-price-current]');
      const compare = panel.querySelector('[data-gm3-compare-price-compare]');
      if (current) {
        const minPrice = Number(current.dataset.priceMin);
        const maxPrice = Number(current.dataset.priceMax);
        if (Number.isFinite(minPrice) && Number.isFinite(maxPrice)) {
          if (current.dataset.priceMinimumOnly === 'true') {
            current.textContent = maxPrice > minPrice ? `From ${formatMoney(minPrice)}` : formatMoney(minPrice);
          } else {
            current.textContent = maxPrice > minPrice
              ? `From ${formatMoney(minPrice)} - ${formatMoney(maxPrice)}`
              : formatMoney(minPrice);
          }
        } else {
          current.textContent = formatMoney(variant.price);
        }
      }
      if (!compare) return;

      const compareAtPrice = Number(variant.compare_at_price || variant.compareAtPrice || 0);
      const price = Number(variant.price || 0);
      const showCompare = compareAtPrice > price;
      compare.hidden = !showCompare;
      compare.textContent = showCompare ? formatMoney(compareAtPrice) : '';
    };

    const getNavOffset = () => {
      const nanoNav =
        document.querySelector('.nano-hero__nav.is-fixed.is-stuck') ||
        document.querySelector('.nano-hero__nav.is-fixed') ||
        document.querySelector('.nano-hero__nav');
      const navRect = nanoNav?.getBoundingClientRect();
      if (navRect?.height) return Math.round(navRect.height);

      const styles = window.getComputedStyle(document.documentElement);
      const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-height')) || 0;
      const headerOffset = Number.parseFloat(styles.getPropertyValue('--header-offset')) || 0;
      return Math.round(headerHeight + headerOffset || 0);
    };

    const syncStickyOffset = () => {
      root.style.setProperty('--gm3-compare-sticky-top', `${getNavOffset()}px`);
    };

    const syncStickyLayout = () => {
      if (!products || !mobileQuery.matches) return;
      const rect = products.getBoundingClientRect();
      const styles = window.getComputedStyle(products);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 12;
      const width = Math.max(0, (rect.width - gap) / 2);

      root.style.setProperty('--gm3-compare-sticky-left', `${Math.round(rect.left)}px`);
      root.style.setProperty('--gm3-compare-sticky-right', `${Math.round(rect.left + width + gap)}px`);
      root.style.setProperty('--gm3-compare-sticky-select-width', `${Math.round(width)}px`);
    };

    const updateSelectStuckState = () => {
      stickyTicking = false;
      if (!products || !mobileQuery.matches) {
        root.classList.remove('is-select-stuck');
        return;
      }

      const navOffset = getNavOffset();
      const productsRect = products.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      const isStuck = productsRect.top <= navOffset && rootRect.bottom > 0;
      if (isStuck) syncStickyLayout();
      root.classList.toggle('is-select-stuck', isStuck);
    };

    const requestSelectStuckState = () => {
      if (stickyTicking) return;
      stickyTicking = true;
      window.requestAnimationFrame(updateSelectStuckState);
    };

    const syncPaymentWidth = (panel) => {
      const selector = panel?.querySelector('[data-gm3-compare-variant-selector]');
      const paymentWrap = panel?.querySelector('.gm3-compare__payment-wrap');
      if (!selector || !paymentWrap) return;

      requestAnimationFrame(() => {
        const width = selector.getBoundingClientRect().width;
        if (width > 0) {
          paymentWrap.style.setProperty('--gm3-compare-payment-width', `${width}px`);
        }
      });
    };

    const setCustomCheckoutLoading = (button, isLoading) => {
      if (!button) return;
      button.classList.toggle('is-loading', isLoading);
      button.toggleAttribute('aria-busy', isLoading);
      if (isLoading) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }
    };

    const submitCustomCheckout = (button) => {
      const panel = button.closest('[data-gm3-compare-panel]');
      const form = button.closest('form') || panel?.querySelector('.gm3-compare__form');
      if (!panel || !form || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      const formData = new FormData(form);
      const variantId = formData.get('id');
      if (!variantId) return;

      setCustomCheckoutLoading(button, true);

      fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: formData,
      })
        .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
        .then(({ ok, json }) => {
          if (!ok || json.status) {
            throw new Error(json.description || json.message || 'Unable to add item to cart.');
          }
          window.location.href = '/checkout';
        })
        .catch((error) => {
          console.warn('[gm3-compare] custom checkout failed', error);
          setCustomCheckoutLoading(button, false);
          const activeVariant = panel.querySelector('[data-gm3-compare-variant-input]')?.value;
          const variantsNode = panel.querySelector('[data-gm3-compare-variants-json]');
          const variants = variantsNode ? JSON.parse(variantsNode.textContent || '[]') : [];
          const variant = variants.find((item) => String(item.id) === String(activeVariant));
          const available = variant?.available !== false;
          button.disabled = !available;
          button.setAttribute('aria-disabled', available ? 'false' : 'true');
        });
    };

    const closeAllMenus = () => {
      root.querySelectorAll('[data-gm3-compare-select-wrap].is-open').forEach((wrap) => {
        wrap.classList.remove('is-open');
        wrap.querySelector('[data-gm3-compare-select-button]')?.setAttribute('aria-expanded', 'false');
      });
      root.querySelectorAll('[data-gm3-compare-variant-selector].is-open').forEach((selector) => {
        selector.classList.remove('is-open');
        selector.querySelector('[data-gm3-compare-option-button]')?.setAttribute('aria-expanded', 'false');
        const panel = selector.querySelector('[data-gm3-compare-option-panel]');
        if (panel) panel.hidden = true;
      });
    };

    const getPanel = (side, blockId) =>
      root.querySelector(`[data-gm3-compare-panel][data-side="${side}"][data-block-id="${CSS.escape(blockId)}"]`);

    const updateSpecs = () => {
      const activePanels = sides
        .map((side) => root.querySelector(`[data-gm3-compare-panel][data-side="${side}"].is-active`))
        .filter(Boolean);
      const rows = root.querySelectorAll('[data-gm3-compare-row]');
      let contentRowCount = 0;

      rows.forEach((row) => {
        let hasContent = false;
        sides.forEach((side) => {
          const target = row.querySelector(`[data-gm3-compare-cell-target="${side}"]`);
          const panel = activePanels.find((item) => item.dataset.side === side);
          const index = row.dataset.gm3CompareRow;
          const source = panel?.querySelector(`[data-gm3-compare-spec-source="${index}"]`);
          const sourceHasContent = source?.dataset.gm3CompareSpecHasContent === 'true';

          if (!target) return;
          target.innerHTML = sourceHasContent ? source.innerHTML : '';
          if (sourceHasContent) hasContent = true;
        });

        if (hasContent) contentRowCount += 1;
        row.hidden = !hasContent || (!specsExpanded && contentRowCount > collapsedSpecLimit);
      });

      if (specToggle) {
        const shouldShowToggle = contentRowCount > collapsedSpecLimit;
        specToggle.hidden = !shouldShowToggle;
        specToggle.setAttribute('aria-expanded', specsExpanded ? 'true' : 'false');
        if (specToggleText) {
          specToggleText.textContent = specsExpanded
            ? specToggle.dataset.showLessText || 'Show Less'
            : specToggle.dataset.showMoreText || 'Show More';
        }
      }
    };

    const updateVariant = (panel) => {
      const jsonNode = panel.querySelector('[data-gm3-compare-variants-json]');
      const variants = jsonNode ? JSON.parse(jsonNode.textContent || '[]') : [];
      const selectedOptions = [];
      const fixedOptionIndex = Number(panel.dataset.variantFilterIndex);
      const fixedOptionValue = panel.dataset.variantFilterValue || '';
      if (Number.isInteger(fixedOptionIndex) && fixedOptionIndex >= 0 && fixedOptionValue) {
        selectedOptions[fixedOptionIndex] = fixedOptionValue;
      }
      panel.querySelectorAll('[data-gm3-compare-option-group]').forEach((group) => {
        const active = group.querySelector('[data-gm3-compare-option].is-selected');
        const optionIndex = Number(group.dataset.optionIndex);
        if (Number.isInteger(optionIndex)) selectedOptions[optionIndex] = active?.dataset.value || '';
      });
      const variant =
        variants.find((item) => {
          const options = normalizeOptions(item);
          return selectedOptions.every((value, index) => !value || options[index] === value);
        }) || variants.find((item) => item.available) || variants[0];

      if (!variant) return;

      const input = panel.querySelector('[data-gm3-compare-variant-input]');
      const label = panel.querySelector('[data-gm3-compare-variant-label]');
      const payment = panel.querySelector('.shopify-payment-button__button');
      const customPayment = panel.querySelector('[data-gm3-compare-custom-checkout]');
      const customPaymentText = panel.querySelector('[data-gm3-compare-custom-checkout-text]');

      if (input) input.value = variant.id;
      // Update each group's label span with the selected value
      panel.querySelectorAll('[data-gm3-compare-option-group]').forEach((group) => {
        const selected = group.querySelector('[data-gm3-compare-option].is-selected');
        const groupLabel = group.querySelector('[data-gm3-compare-variant-label]');
        if (groupLabel && selected) {
          groupLabel.textContent = selected.textContent.trim();
        }
      });
      updatePrice(panel, variant);

      // Switch media image only when show_variant is enabled (variant selectors exist)
      const hasVariantSelector = panel.querySelector('[data-gm3-compare-variant-selector]');
      if (hasVariantSelector) {
        const mediaEl = panel.querySelector('[data-gm3-compare-media]');
        const currentImg = mediaEl?.querySelector('.gm3-compare__image');
        // Prefer the image on the fully matched variant. The option image is only a fallback
        // for themes where the variant JSON does not include featured_image.
        let variantImgUrl = getVariantImageUrl(variant);
        const selectedButtons = panel.querySelectorAll('[data-gm3-compare-option].is-selected');
        if (!variantImgUrl) {
          selectedButtons.forEach((btn) => {
            if (btn.dataset.variantImage) variantImgUrl = btn.dataset.variantImage;
          });
        }
        if (!variantImgUrl) variantImgUrl = mediaEl?.dataset.baseImage || '';
        if (currentImg && variantImgUrl && currentImg.src !== variantImgUrl) {
          currentImg.src = variantImgUrl;
          currentImg.removeAttribute('srcset');
          currentImg.removeAttribute('sizes');
        }
      }

      if (payment) {
        payment.disabled = !variant.available;
        payment.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
      }
      if (customPayment) {
        customPayment.disabled = !variant.available;
        customPayment.classList.toggle('is-sold-out', !variant.available);
        customPayment.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
      }
      if (customPaymentText && customPayment) {
        customPaymentText.textContent = variant.available
          ? customPayment.dataset.buttonText || customPaymentText.textContent
          : customPayment.dataset.soldOutText || customPaymentText.textContent;
      }
      syncPaymentWidth(panel);

      panel.querySelectorAll('[data-gm3-compare-option-group]').forEach((group) => {
        const optionIndex = Number(group.dataset.optionIndex);
        group.querySelectorAll('[data-gm3-compare-option]').forEach((button) => {
          const trial = selectedOptions.slice();
          trial[optionIndex] = button.dataset.value || '';
          const possible = variants.some((item) => {
            const options = normalizeOptions(item);
            return trial.every((value, index) => !value || options[index] === value) && item.available !== false;
          });
          button.disabled = !possible;
          button.setAttribute('aria-disabled', possible ? 'false' : 'true');
        });
      });
    };

    const activateBlock = (side, blockId) => {
      const panel = getPanel(side, blockId);
      if (!panel) return;

      root.querySelectorAll(`[data-gm3-compare-panel][data-side="${side}"]`).forEach((item) => {
        item.classList.toggle('is-active', item === panel);
        item.hidden = item !== panel;
      });

      const wrap = root.querySelector(`[data-gm3-compare-select-wrap][data-side="${side}"]`);
      const buttonText = wrap?.querySelector('[data-gm3-compare-select-text]');
      wrap?.querySelectorAll('[data-gm3-compare-select-option]').forEach((option) => {
        const active = option.dataset.blockId === blockId;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active && buttonText) buttonText.textContent = option.textContent.trim();
      });

      updateVariant(panel);
      updateSpecs();
      syncPaymentWidth(panel);
    };

    root.addEventListener('click', (event) => {
      const specToggleButton = event.target.closest('[data-gm3-compare-spec-toggle]');
      if (specToggleButton && root.contains(specToggleButton)) {
        event.preventDefault();
        specsExpanded = !specsExpanded;
        updateSpecs();
        return;
      }

      const customCheckoutButton = event.target.closest('[data-gm3-compare-custom-checkout]');
      if (customCheckoutButton && root.contains(customCheckoutButton)) {
        event.preventDefault();
        submitCustomCheckout(customCheckoutButton);
        return;
      }

      const selectButton = event.target.closest('[data-gm3-compare-select-button]');
      if (selectButton && root.contains(selectButton)) {
        event.preventDefault();
        const wrap = selectButton.closest('[data-gm3-compare-select-wrap]');
        const wasOpen = wrap?.classList.contains('is-open');
        closeAllMenus();
        if (wrap && !wasOpen) {
          wrap.classList.add('is-open');
          selectButton.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      const selectOption = event.target.closest('[data-gm3-compare-select-option]');
      if (selectOption && root.contains(selectOption)) {
        event.preventDefault();
        activateBlock(selectOption.dataset.side, selectOption.dataset.blockId);
        closeAllMenus();
        return;
      }

      const optionButton = event.target.closest('[data-gm3-compare-option-button]');
      if (optionButton && root.contains(optionButton)) {
        event.preventDefault();
        event.stopPropagation();
        if (optionButton.disabled) return;
        const selector = optionButton.closest('[data-gm3-compare-variant-selector]');
        if (!selector) return;
        const wasOpen = selector.classList.contains('is-open');
        closeAllMenus();
        selector.classList.toggle('is-open', !wasOpen);
        optionButton.setAttribute('aria-expanded', !wasOpen ? 'true' : 'false');
        const panel = selector.querySelector('[data-gm3-compare-option-panel]');
        if (panel) panel.hidden = wasOpen;
        return;
      }

      const variantOption = event.target.closest('[data-gm3-compare-option]');
      if (variantOption && root.contains(variantOption)) {
        event.preventDefault();
        event.stopPropagation();
        if (variantOption.disabled) return;
        const group = variantOption.closest('[data-gm3-compare-option-group]');
        const panel = variantOption.closest('[data-gm3-compare-panel]');
        group?.querySelectorAll('[data-gm3-compare-option]').forEach((button) => {
          const active = button === variantOption;
          button.classList.toggle('is-selected', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        const selector = variantOption.closest('[data-gm3-compare-variant-selector]');
        selector?.classList.remove('is-open');
        selector?.querySelector('[data-gm3-compare-option-button]')?.setAttribute('aria-expanded', 'false');
        selector?.querySelector('[data-gm3-compare-option-panel]')?.setAttribute('hidden', '');
        if (panel) updateVariant(panel);
      }
    });

    document.addEventListener('click', (event) => {
      if (root.contains(event.target)) return;
      closeAllMenus();
    });

    root.querySelectorAll('[data-gm3-compare-panel]').forEach(updateVariant);
    syncStickyOffset();
    requestSelectStuckState();
    sides.forEach((side) => {
      const firstOption = root.querySelector(`[data-gm3-compare-select-option][data-side="${side}"].is-active`)
        || root.querySelector(`[data-gm3-compare-select-option][data-side="${side}"]`);
      if (firstOption) activateBlock(side, firstOption.dataset.blockId);
    });

    window.addEventListener('resize', () => {
      syncStickyOffset();
      requestSelectStuckState();
      root.querySelectorAll('[data-gm3-compare-panel].is-active').forEach(syncPaymentWidth);
    });
    window.addEventListener('scroll', requestSelectStuckState, { passive: true });
    mobileQuery.addEventListener?.('change', () => {
      syncStickyOffset();
      requestSelectStuckState();
    });
    document.fonts?.ready?.then(() => {
      root.querySelectorAll('[data-gm3-compare-panel].is-active').forEach(syncPaymentWidth);
    }).catch(() => {});
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-gm3-compare]').forEach(initCompare);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
